import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Book, 
  Edit, 
  Trash, 
  Plus, 
  Search, 
  Image, 
  Download,
  Upload,
  BarChart3,
  Globe,
  Type,
  Languages,
  Wand2,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const WordDatabaseAdmin = () => {
  const [words, setWords] = useState([]);
  const [filteredWords, setFilteredWords] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedLanguageLevel, setSelectedLanguageLevel] = useState('all');
  const [selectedWordType, setSelectedWordType] = useState('all');
  const [selectedWord, setSelectedWord] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [selectedWords, setSelectedWords] = useState([]);
  const [isBulkTranslating, setIsBulkTranslating] = useState(false);
  const [isBulkGeneratingImages, setIsBulkGeneratingImages] = useState(false);
  const { toast } = useToast();

  // Hilfsfunktionen
  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-500',
      'A2': 'bg-green-600', 
      'B1': 'bg-yellow-500',
      'B2': 'bg-yellow-600',
      'C1': 'bg-red-500',
      'C2': 'bg-red-600'
    };
    return colors[level] || 'bg-gray-500';
  };

  // Konstanten
  const LANGUAGES = {
    'de': 'Deutsch',
    'en': 'English', 
    'fr': 'Français',
    'es': 'Español',
    'it': 'Italiano',
    'tr': 'Türkçe'
  };

  const LANGUAGE_LEVELS = {
    'A1': 'A1 - Anfänger',
    'A2': 'A2 - Grundkenntnisse',
    'B1': 'B1 - Mittelstufe',
    'B2': 'B2 - Selbstständig',
    'C1': 'C1 - Fortgeschritten',
    'C2': 'C2 - Fachkundig'
  };

  const WORD_TYPES = {
    'noun': 'Substantiv',
    'verb': 'Verb',
    'adjective': 'Adjektiv',
    'adverb': 'Adverb',
    'preposition': 'Präposition',
    'article': 'Artikel',
    'pronoun': 'Pronomen',
    'conjunction': 'Konjunktion',
    'interjection': 'Interjektion'
  };

  // API-Aufrufe
  const fetchWords = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      let url = `${backendUrl}/api/words?limit=1000`;
      
      // Füge Filter-Parameter hinzu
      const params = new URLSearchParams();
      if (selectedLanguage !== 'all') params.append('source_language', selectedLanguage);
      if (selectedLanguageLevel !== 'all') params.append('language_level', selectedLanguageLevel);
      if (selectedWordType !== 'all') params.append('word_type', selectedWordType);
      
      if (params.toString()) {
        url += '&' + params.toString();
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setWords(data);
        setFilteredWords(data);
      } else {
        toast({
          title: "Fehler",
          description: "Wörter konnten nicht geladen werden",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error fetching words:', error);
      toast({
        title: "Fehler",
        description: "Verbindungsfehler beim Laden der Wörter",
        variant: "destructive"
      });
    }
  };

  const fetchStatistics = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/words/statistics`);
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const deleteWord = async (wordId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/words/${wordId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await fetchWords();
        await fetchStatistics();
        toast({
          title: "Erfolg",
          description: "Wort wurde erfolgreich gelöscht"
        });
      } else {
        toast({
          title: "Fehler",
          description: "Wort konnte nicht gelöscht werden",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting word:', error);
      toast({
        title: "Fehler",
        description: "Verbindungsfehler beim Löschen",
        variant: "destructive"
      });
    }
  };

  const updateWord = async (wordId, updateData) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/words/${wordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        await fetchWords();
        await fetchStatistics();
        setIsEditDialogOpen(false);
        toast({
          title: "Erfolg",
          description: "Wort wurde erfolgreich aktualisiert"
        });
      } else {
        toast({
          title: "Fehler",
          description: "Wort konnte nicht aktualisiert werden",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating word:', error);
      toast({
        title: "Fehler",
        description: "Verbindungsfehler beim Aktualisieren",
        variant: "destructive"
      });
    }
  };

  const createWord = async (wordData) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/words`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(wordData)
      });
      
      if (response.ok) {
        await fetchWords();
        await fetchStatistics();
        setIsCreateDialogOpen(false);
        toast({
          title: "Erfolg",
          description: "Neues Wort wurde erfolgreich erstellt"
        });
      } else {
        toast({
          title: "Fehler",
          description: "Wort konnte nicht erstellt werden",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error creating word:', error);
      toast({
        title: "Fehler",
        description: "Verbindungsfehler beim Erstellen",
        variant: "destructive"
      });
    }
  };

  const extractWordsFromStory = async (storyId, storyContent, sourceLanguage) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/words/extract-from-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          story_id: storyId,
          story_content: storyContent,
          source_language: sourceLanguage
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        await fetchWords();
        await fetchStatistics();
        toast({
          title: "Erfolg",
          description: result.message
        });
      }
    } catch (error) {
      console.error('Error extracting words:', error);
      toast({
        title: "Fehler",
        description: "Wort-Extraktion fehlgeschlagen",
        variant: "destructive" 
      });
    }
  };

  const bulkTranslateWords = async (targetLanguages = ['en', 'es', 'fr']) => {
    if (selectedWords.length === 0) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie mindestens ein Wort aus",
        variant: "destructive"
      });
      return;
    }

    setIsBulkTranslating(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/translation/bulk-translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          word_ids: selectedWords,
          target_languages: targetLanguages,
          overwrite_existing: false
        })
      });

      if (response.ok) {
        const result = await response.json();
        await fetchWords();
        await fetchStatistics();
        setSelectedWords([]);
        toast({
          title: "Erfolg",
          description: result.message
        });
      } else {
        throw new Error('Bulk-Übersetzung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error bulk translating:', error);
      toast({
        title: "Fehler",
        description: "Bulk-Übersetzung fehlgeschlagen",
        variant: "destructive"
      });
    } finally {
      setIsBulkTranslating(false);
    }
  };

  const bulkGenerateImages = async () => {
    if (selectedWords.length === 0) {
      toast({
        title: "Fehler", 
        description: "Bitte wählen Sie mindestens ein Wort aus",
        variant: "destructive"
      });
      return;
    }

    setIsBulkGeneratingImages(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/images/bulk-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedWords)
      });

      if (response.ok) {
        const result = await response.json();
        await fetchWords();
        await fetchStatistics();
        setSelectedWords([]);
        toast({
          title: "Erfolg",
          description: result.message
        });
      } else {
        throw new Error('Bulk-Bildgenerierung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error bulk generating images:', error);
      toast({
        title: "Fehler",
        description: "Bulk-Bildgenerierung fehlgeschlagen",
        variant: "destructive"
      });
    } finally {
      setIsBulkGeneratingImages(false);
    }
  };

  const generateImageForWord = async (wordId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/images/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          word_id: wordId
        })
      });

      if (response.ok) {
        const result = await response.json();
        await fetchWords();
        toast({
          title: "Erfolg",
          description: "Bild erfolgreich generiert"
        });
      } else {
        throw new Error('Bildgenerierung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: "Fehler",
        description: "Bildgenerierung fehlgeschlagen",
        variant: "destructive"
      });
    }
  };

  // Filter-Logik
  useEffect(() => {
    let filtered = words;

    if (searchTerm) {
      filtered = filtered.filter(word => 
        word.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (word.mnemonic_keyword && word.mnemonic_keyword.toLowerCase().includes(searchTerm.toLowerCase())) ||
        Object.values(word.translations).some(translation => 
          translation.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(word => word.source_language === selectedLanguage);
    }

    if (selectedLanguageLevel !== 'all') {
      filtered = filtered.filter(word => word.language_level === selectedLanguageLevel);
    }

    if (selectedWordType !== 'all') {
      filtered = filtered.filter(word => word.word_type === selectedWordType);
    }

    setFilteredWords(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedLanguage, selectedLanguageLevel, selectedWordType, words]);

  // Paginierung
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentWords = filteredWords.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredWords.length / itemsPerPage);

  // Initial Load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchWords(), fetchStatistics()]);
      setLoading(false);
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Lade Wörterdatenbank...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Wörterdatenbank Administration</h1>
            <p className="text-gray-600 mt-2">
              Verwalten Sie alle Wörter mit Schlüsselwort-Methode und visuellen Merkhilfen
            </p>
          </div>
          <div className="flex space-x-2">
            <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-[#4ECDC4] hover:bg-[#45B7B8]">
              <Plus className="h-4 w-4 mr-2" />
              Neues Wort
            </Button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedWords.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="font-medium text-orange-800">
                    {selectedWords.length} Wort{selectedWords.length > 1 ? 'e' : ''} ausgewählt
                  </span>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => bulkTranslateWords()}
                    disabled={isBulkTranslating}
                  >
                    {isBulkTranslating ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Languages className="h-4 w-4 mr-2" />
                    )}
                    Übersetzen
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={bulkGenerateImages}
                    disabled={isBulkGeneratingImages}
                  >
                    {isBulkGeneratingImages ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4 mr-2" />
                    )}
                    Bilder generieren
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedWords([])}
                  >
                    Auswahl aufheben
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statistiken */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Book className="h-8 w-8 text-[#4ECDC4]" />
                  <div>
                    <p className="text-2xl font-bold">{statistics.total_words}</p>
                    <p className="text-sm text-gray-600">Gesamt Wörter</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Globe className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{Object.keys(statistics.by_language || {}).length}</p>
                    <p className="text-sm text-gray-600">Sprachen</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{statistics.completion_rate?.mnemonics || 0}%</p>
                    <p className="text-sm text-gray-600">Mit Merksätzen</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Image className="h-8 w-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{statistics.completion_rate?.images || 0}%</p>
                    <p className="text-sm text-gray-600">Mit Bildern</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter und Suche */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="search">Suche</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="search"
                    placeholder="Wort oder Übersetzung suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="language">Sprache</Label>
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger>
                    <SelectValue placeholder="Alle Sprachen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Sprachen</SelectItem>
                    {Object.entries(LANGUAGES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="level">Sprachlevel</Label>
                <Select value={selectedLanguageLevel} onValueChange={setSelectedLanguageLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Alle Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Level</SelectItem>
                    {Object.entries(LANGUAGE_LEVELS).map(([level, name]) => (
                      <SelectItem key={level} value={level}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="wordtype">Wortart</Label>
                <Select value={selectedWordType} onValueChange={setSelectedWordType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Alle Wortarten" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Wortarten</SelectItem>
                    {Object.entries(WORD_TYPES).map(([type, name]) => (
                      <SelectItem key={type} value={type}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedLanguage('all');
                    setSelectedLanguageLevel('all');
                    setSelectedWordType('all');
                  }}
                >
                  Filter zurücksetzen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wörter-Tabelle */}
        <Card>
          <CardHeader>
            <CardTitle>
              Wörter ({filteredWords.length} von {words.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedWords.length === currentWords.length && currentWords.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedWords(currentWords.map(w => w.id));
                        } else {
                          setSelectedWords([]);
                        }
                      }}
                      className="rounded"
                    />
                  </TableHead>
                  <TableHead>Wort</TableHead>
                  <TableHead>Sprache</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Wortart</TableHead>
                  <TableHead>Übersetzungen</TableHead>
                  <TableHead>Schlüsselwort</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentWords.map((word) => (
                  <TableRow key={word.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedWords.includes(word.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedWords([...selectedWords, word.id]);
                          } else {
                            setSelectedWords(selectedWords.filter(id => id !== word.id));
                          }
                        }}
                        className="rounded"
                      />
                    </TableCell>
                    <TableCell className="font-medium">{word.word}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {LANGUAGES[word.source_language] || word.source_language}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={`${getLevelColor(word.language_level)} text-white`}
                      >
                        {word.language_level}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {WORD_TYPES[word.word_type] || word.word_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(word.translations || {}).map(([lang, translation]) => (
                          <Badge key={lang} variant="outline" className="text-xs">
                            {translation}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {word.mnemonic_keyword ? (
                        <Badge className="bg-green-100 text-green-800">
                          {word.mnemonic_keyword}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {word.mnemonic_sentence && (
                          <Badge className="bg-blue-100 text-blue-800">Merksatz</Badge>
                        )}
                        {word.mnemonic_image_url && (
                          <Badge className="bg-purple-100 text-purple-800">Bild</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {!word.mnemonic_image_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => generateImageForWord(word.id)}
                            title="Bild generieren"
                          >
                            <Wand2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedWord(word);
                            setIsEditDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteWord(word.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Paginierung */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Zurück
                </Button>
                <span className="text-sm text-gray-600">
                  Seite {currentPage} von {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Weiter
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog - wird im nächsten Teil implementiert */}
      <WordEditDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        word={selectedWord}
        onSave={updateWord}
        languages={LANGUAGES}
        wordTypes={WORD_TYPES}
      />

      {/* Create Dialog - wird im nächsten Teil implementiert */}
      <WordCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreate={createWord}
        languages={LANGUAGES}
        wordTypes={WORD_TYPES}
      />
    </div>
  );
};

// Platzhalter-Komponenten für Dialoge (werden als nächstes implementiert)
const WordEditDialog = ({ isOpen, onClose, word, onSave, languages, wordTypes }) => {
  if (!isOpen || !word) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <h3 className="text-lg font-semibold mb-4">Wort bearbeiten: {word.word}</h3>
        <p className="text-gray-600">Dialog wird in der nächsten Phase implementiert...</p>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
        </div>
      </div>
    </div>
  );
};

const WordCreateDialog = ({ isOpen, onClose, onCreate, languages, wordTypes }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <h3 className="text-lg font-semibold mb-4">Neues Wort erstellen</h3>
        <p className="text-gray-600">Dialog wird in der nächsten Phase implementiert...</p>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
        </div>
      </div>
    </div>
  );
};

export default WordDatabaseAdmin;